import { useMemo, useRef } from 'react';
import { useReportMeasurement } from '@/context/ReportMeasurementContext';
import {
    DEFAULT_SIGNATURE_HEIGHT,
    FIXED_REPORT_MEASUREMENT_IDS,
    REPORT_SIGNATURE_MEASUREMENT_ID,
} from './pagination/constants';
import { PageManager } from './pagination/PageManager';
import { processTestsSection } from './pagination/process-tests';
import { processTextSection } from './pagination/process-text';
import { RenderableSection } from './pagination/types';
import { Section } from './types';
import { ReportData } from './types';
import type { ReportFootnotePlan } from './footnotes';

// Re-export type for consumers
export type { RenderableSection };

export function useReportPagination(
    sections: Section[],
    data: ReportData,
    editingBlockId?: string | number | null,
    footnotePlan?: ReportFootnotePlan,
    /**
     * Holds the current page layout while a block drag is in flight. Async
     * measurements re-chunk the pages, which would swap the `test_blocks_*`
     * droppable ids out from under @hello-pangea/dnd mid-drag.
     */
    freeze = false,
) {
    const { measurements, areMeasurementsReady } = useReportMeasurement();
    const isPaginationReady = areMeasurementsReady(FIXED_REPORT_MEASUREMENT_IDS);

    const computed = useMemo(() => {
        const pageManager = new PageManager(measurements);

        // A tabela de identificação pode ocupar quase toda a página 1 quando a
        // Finalidade é longa; nesse caso a página 1 fica só com cabeçalho +
        // tabela e o conteúdo começa na página 2.
        if (sections.length > 0 && pageManager.shouldSkipFirstPage()) {
            pageManager.startNewPage();
        }

        sections.forEach((section) => {
            // Check if we need a new page based on generic height check
            // (Note: This was present in the original code logic before section processing)
            pageManager.forceNewPageIfFull();

            if (section.id === 'tests') {
                processTestsSection({
                    section,
                    data,
                    measurements,
                    pageManager,
                    editingBlockId,
                    footnotePlan,
                });
            } else {
                const originalContent =
                    typeof section.content === 'string'
                        ? section.content
                        : (
                              data[section.id as keyof ReportData] as
                                  | {
                                        content?: string;
                                    }
                                  | undefined
                          )?.content || '';

                processTextSection({
                    section,
                    content: originalContent,
                    secId: section.id,
                    measuredHeight: undefined,
                    pageManager,
                    measurements,
                    data,
                });
            }
        });

        if (sections.length > 0) {
            const signatureHeight =
                measurements[REPORT_SIGNATURE_MEASUREMENT_ID] || DEFAULT_SIGNATURE_HEIGHT;

            pageManager.ensureTrailingSpace(signatureHeight);
        }

        return {
            totalPages: pageManager.pages.length,
            pageSections: pageManager.pages,
            pageFootnotes: pageManager.pageFootnotes,
            sectionPageMap: pageManager.pageMap,
        };
    }, [sections, measurements, data, editingBlockId, footnotePlan]);

    const frozenRef = useRef(computed);
    if (!freeze) {
        frozenRef.current = computed;
    }
    const { totalPages, pageSections, pageFootnotes, sectionPageMap } = freeze
        ? frozenRef.current
        : computed;

    const getPageSections = (pageNum: number) => {
        return pageSections[pageNum - 1] || [];
    };

    const getPageFootnotes = (pageNum: number) => {
        return pageFootnotes[pageNum - 1] || [];
    };

    return {
        totalPages,
        getPageSections,
        getPageFootnotes,
        sectionPageMap,
        isPaginationReady,
    };
}
